import React from "react";
import { connect } from "react-redux";
import { IAppState } from "../../state/Store";
import { moveNextAlert, updatePrompt } from "../../state/system/Alerts";
import AlertDialogPrompt from "../../types/system/AlertDialogPrompt";
import Dialog from "../../components/dialog/Dialog";
import DialogCancel from "../../components/dialog/DialogCancel";
import DialogContent from "../../components/dialog/DialogContent";
import DialogFooter from "../../components/dialog/DialogFooter";
import DialogOK from "../../components/dialog/DialogOK";
import Panel from "../../components/panels/Panel";
import Checkbox from "../../components/inputform/Checkbox";
import { Alert, AlertTypes } from "../../types/system/AlertEntry";
import { Side } from "../../types/system/Layout";

import "./AlertDialog.scss";
import DatePicker from "../inputform/DatePicker";
import TimePicker from "../inputform/TimePicker";
import Textbox from "../inputform/Textbox";
import DropDown from "../inputform/DropDown";

interface AlertDialogProps {
    open?: boolean;
    entry?: Alert;

    moveNextAlert: () => void;
    updatePrompt: (prompt: AlertDialogPrompt, value: any) => void;
}

interface AlertDialogState {}

class AlertDialog extends React.Component<AlertDialogProps, AlertDialogState> {
    okClick() {
        this.props.entry.promiseWrapper.resolve({
            response: true,
            buttonClicked:
                this.props.entry?.okText ??
                (this.props.entry?.type === AlertTypes.Confirm ? "Yes" : "OK"),
            promptResults: this.props.entry.prompts,
        });
        this.props.moveNextAlert();
    }
    cancelClick() {
        this.props.entry.promiseWrapper.resolve({
            response: false,
            buttonClicked:
                this.props.entry?.cancelText ??
                (this.props.entry?.type === AlertTypes.Confirm
                    ? "No"
                    : "Cancel"),
            promptResults: this.props.entry.prompts,
        });
        this.props.moveNextAlert();
    }
    buttonClick(button: string) {
        this.props.entry.promiseWrapper.resolve({
            response: true,
            buttonClicked: button,
            promptResults: this.props.entry.prompts,
        });
    }
    promptChange(prompt: AlertDialogPrompt, value: any) {
        this.props.updatePrompt(prompt, value);
    }

    render() {
        let display = this.props.entry?.text?.replace(/\n/g, "<br>") ?? "";
        let title =
            this.props.entry?.title ??
            (this.props.entry?.type === AlertTypes.Prompt
                ? "Enter Information"
                : this.props.entry?.type === AlertTypes.Confirm
                ? "Confirm"
                : "Information");

        return (
            <Dialog
                dialogTitle={title}
                dialogIcon={this.props.entry?.icon}
                dialogIconStyle={this.props.entry?.styleColor}
                showDialog={this.props.open}
                canResize={false}
                width={this.props.entry?.width ?? 600}
                zIndex={99999}>
                <DialogContent>
                    <div className="alert-dialog">
                        {display && (
                            <div
                                className="text"
                                dangerouslySetInnerHTML={{
                                    __html: display,
                                }}></div>
                        )}

                        {this.props.entry?.prompts?.map((prompt, index) => (
                            <Panel
                                key={index}
                                bufferSide={Side.All}
                                clearanceSide={
                                    index > 0 ? Side.Top : undefined
                                }>
                                <div
                                    className="text"
                                    dangerouslySetInnerHTML={{
                                        __html: prompt.display,
                                    }}></div>
                                {prompt.error && (
                                    <div
                                        className="error-message"
                                        dangerouslySetInnerHTML={{
                                            __html: prompt.error,
                                        }}></div>
                                )}
                                {prompt.type === "text" && (
                                    <Textbox
                                        value={prompt.value}
                                        onChange={value =>
                                            this.promptChange(prompt, value)
                                        }
                                    />
                                )}
                                {prompt.type === "date" && (
                                    <DatePicker
                                        value={prompt.value ?? new Date()}
                                        minDate={prompt.minDate}
                                        maxDate={prompt.maxDate}
                                        onChange={value =>
                                            this.promptChange(prompt, value)
                                        }
                                    />
                                )}
                                {prompt.type === "time" && (
                                    <TimePicker
                                        value={prompt.value ?? ""}
                                        onChange={value =>
                                            this.promptChange(prompt, value)
                                        }
                                    />
                                )}
                                {prompt.type === "number" && (
                                    <Textbox
                                        type="number"
                                        value={prompt.value ?? 0}
                                        min={prompt.minNumber}
                                        max={prompt.maxNumber}
                                        onChange={value =>
                                            this.promptChange(
                                                prompt,
                                                Number(value),
                                            )
                                        }
                                    />
                                )}
                                {prompt.type === "boolean" && (
                                    <Checkbox
                                        value={prompt.value}
                                        onChange={value =>
                                            this.promptChange(prompt, value)
                                        }
                                    />
                                )}
                                {prompt.type === "select" && (
                                    <DropDown
                                        noType={true}
                                        fullWidth={true}
                                        value={prompt.value}
                                        options={prompt.options ?? []}
                                        onChange={value =>
                                            this.promptChange(prompt, value)
                                        }
                                    />
                                )}
                            </Panel>
                        ))}
                    </div>
                </DialogContent>
                <DialogFooter>
                    {this.props.entry?.buttons?.map(button => {
                        return (
                            (button.toLowerCase().includes("ok") && (
                                <DialogOK
                                    text={button}
                                    onClick={() =>
                                        this.buttonClick(button)
                                    }></DialogOK>
                            )) || (
                                <DialogCancel
                                    text={button}
                                    onClick={() =>
                                        this.buttonClick(button)
                                    }></DialogCancel>
                            )
                        );
                    })}
                    {!this.props.entry?.buttons &&
                        this.props.entry?.type !== AlertTypes.Alert && (
                            <DialogCancel
                                text={
                                    this.props.entry?.cancelText ??
                                    (this.props.entry?.type ===
                                    AlertTypes.Confirm
                                        ? "No"
                                        : "Cancel")
                                }
                                onClick={() =>
                                    this.cancelClick()
                                }></DialogCancel>
                        )}
                    {!this.props.entry?.buttons && (
                        <DialogOK
                            text={
                                this.props.entry?.okText ??
                                (this.props.entry?.type === AlertTypes.Confirm
                                    ? "Yes"
                                    : "OK")
                            }
                            onClick={() => this.okClick()}></DialogOK>
                    )}
                </DialogFooter>
            </Dialog>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { alert } = state.db;
    return {
        open: alert.open,
        entry: alert.current,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        moveNextAlert: () => dispatch(moveNextAlert()),
        updatePrompt: (prompt: AlertDialogPrompt, value: any) =>
            dispatch(updatePrompt({ prompt: prompt, value: value })),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertDialog);
